import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import ModalNew from "../Modal";
import { PopupHeader, PopupContent } from "../common";
import FieldLabel from "../../forms/FieldLabel";
import DateInput from "../../forms/DateInput";
import {
  formatSSNInput,
  cleanSSN,
} from "../../../../utils/formatting/ssnFormatter";

const dependentSchema = z.object({
  firstname: z.string().min(1, "First name is required"),
  lastname: z.string().min(1, "Last name is required"),
  ssnItin: z
    .string()
    .min(1, "SSN / ITIN is required")
    .refine((val) => {
      const digits = val.replace(/\D/g, "");
      return digits.length === 9;
    }, "SSN / ITIN must be 9 digits"),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  monthsLivedWith: z
    .string()
    .min(1, "Months lived with you is required")
    .refine((val) => {
      const num = parseInt(val, 10);
      return !isNaN(num) && num >= 0 && num <= 12;
    }, "Months must be between 0 and 12"),
  relationship: z.enum(["qualifying_child", "qualifying_relative"], {
    required_error: "Relationship is required",
  }),
});

function DependentModal({
  isOpen,
  onClose,
  onSubmit,
  dependent = null, // For editing existing dependent
}) {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    reset,
  } = useForm({
    resolver: zodResolver(dependentSchema),
    defaultValues: dependent || {
      firstname: "",
      lastname: "",
      ssnItin: "",
      dateOfBirth: "",
      monthsLivedWith: "",
      relationship: "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const relationshipValue = watch("relationship");

  // Reset form when dependent prop changes (for editing)
  useEffect(() => {
    if (dependent) {
      reset({
        firstname: dependent.firstname || "",
        lastname: dependent.lastname || "",
        ssnItin: formatSSNInput(dependent.ssnItin || ""), // Format existing SSN
        dateOfBirth: dependent.dateOfBirth || "",
        monthsLivedWith: dependent.monthsLivedWith || "",
        relationship: dependent.relationship || "",
      });
    } else {
      reset({
        firstname: "",
        lastname: "",
        ssnItin: "",
        dateOfBirth: "",
        monthsLivedWith: "",
        relationship: "",
      });
    }
  }, [dependent, reset]);

  const handleFormSubmit = async (data) => {
    if (onSubmit) {
      // Clean SSN before submitting (remove formatting)
      const cleanedData = {
        ...data,
        ssnItin: cleanSSN(data.ssnItin),
      };
      const success = await onSubmit(cleanedData);
      if (success) {
        reset();
        onClose();
      }
    }
  };

  const handleClose = () => {
    reset();
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalNew
      className="content-modal"
      style={{ display: isDatePickerOpen ? "none" : undefined }}
    >
      <PopupHeader
        title={dependent ? "Edit Dependent" : "Add Dependent"}
        setOpenState={(isOpen) => {
          if (!isOpen) {
            handleClose();
          }
        }}
        icon={`${process.env.PUBLIC_URL}/images/icons/user_blue_popup.svg`}
        variant="compact"
      />

      <PopupContent>
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="unified-modal-form"
          noValidate
        >
          <div className="modal-content">
            <div
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              {/* First Name & Last Name Row */}
              <div style={{ display: "flex", gap: "20px" }}>
                <div className="form-field" style={{ flex: 1 }}>
                  <FieldLabel label="First Name" required />
                  <input
                    type="text"
                    className="form-input"
                    {...register("firstname")}
                  />
                  {errors.firstname && (
                    <span style={{ color: "red", fontSize: "12px" }}>
                      {errors.firstname.message}
                    </span>
                  )}
                </div>

                <div className="form-field" style={{ flex: 1 }}>
                  <FieldLabel label="Last Name" required />
                  <input
                    type="text"
                    className="form-input"
                    {...register("lastname")}
                  />
                  {errors.lastname && (
                    <span style={{ color: "red", fontSize: "12px" }}>
                      {errors.lastname.message}
                    </span>
                  )}
                </div>
              </div>

              {/* SSN / ITIN */}
              <div className="form-field">
                <FieldLabel label="SSN / ITIN" required />
                <input
                  type="text"
                  className="form-input"
                  placeholder="XXX-XX-XXXX"
                  maxLength="11"
                  value={watch("ssnItin") || ""}
                  onChange={(e) => {
                    const formatted = formatSSNInput(e.target.value);
                    setValue("ssnItin", formatted, {
                      shouldValidate: true,
                      shouldTouch: true,
                      shouldDirty: true,
                    });
                  }}
                  {...(() => {
                    const {
                      onChange: _onChange,
                      value: _value,
                      ...rest
                    } = register("ssnItin");
                    return rest;
                  })()}
                />
                {errors.ssnItin && (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    {errors.ssnItin.message}
                  </span>
                )}
              </div>

              {/* Date of Birth & Months Lived With You Row */}
              <div style={{ display: "flex", gap: "20px" }}>
                <div className="form-field" style={{ flex: 1 }}>
                  <FieldLabel label="Date of Birth" required />
                  <DateInput
                    name="dateOfBirth"
                    label="Date of Birth"
                    value={watch("dateOfBirth") || ""}
                    onOpen={() => setIsDatePickerOpen(true)}
                    onClose={() => setIsDatePickerOpen(false)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setValue("dateOfBirth", value, {
                        shouldValidate: true,
                        shouldTouch: true,
                        shouldDirty: true,
                      });
                    }}
                    onBlur={(e) => {
                      if (e.target.name) {
                        trigger("dateOfBirth", { shouldFocus: false });
                      }
                    }}
                    {...(() => {
                      const {
                        onChange: _onChange,
                        onBlur: _onBlur,
                        ...rest
                      } = register("dateOfBirth");
                      return rest;
                    })()}
                  />
                  {errors.dateOfBirth && (
                    <span style={{ color: "red", fontSize: "12px" }}>
                      {errors.dateOfBirth.message}
                    </span>
                  )}
                </div>

                <div className="form-field" style={{ flex: 1 }}>
                  <FieldLabel label="Months Lived With You" required />
                  <input
                    type="number"
                    className="form-input"
                    min="0"
                    max="12"
                    {...register("monthsLivedWith")}
                  />
                  {errors.monthsLivedWith && (
                    <span style={{ color: "red", fontSize: "12px" }}>
                      {errors.monthsLivedWith.message}
                    </span>
                  )}
                </div>
              </div>

              {/* Relationship */}
              <div className="form-field">
                <FieldLabel label="Relationship" required />
                <div className="checkbox-container">
                  <div className="checkbox-item">
                    <div className="checkbox-representation">
                      <input
                        type="radio"
                        name="relationship"
                        id="relationship_qualifying_child"
                        value="qualifying_child"
                        checked={relationshipValue === "qualifying_child"}
                        onChange={(e) => {
                          setValue("relationship", e.target.value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          trigger("relationship");
                        }}
                      />
                      <label htmlFor="relationship_qualifying_child">
                        Qualifying Child
                      </label>
                    </div>
                  </div>
                  <div className="checkbox-item">
                    <div className="checkbox-representation">
                      <input
                        type="radio"
                        name="relationship"
                        id="relationship_qualifying_relative"
                        value="qualifying_relative"
                        checked={relationshipValue === "qualifying_relative"}
                        onChange={(e) => {
                          setValue("relationship", e.target.value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          trigger("relationship");
                        }}
                      />
                      <label htmlFor="relationship_qualifying_relative">
                        Qualifying Relative
                      </label>
                    </div>
                  </div>
                </div>
                {/* Hidden input for react-hook-form validation */}
                <input
                  type="hidden"
                  {...register("relationship", {
                    required: "Relationship is required",
                  })}
                />
                {errors.relationship && (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    {errors.relationship.message}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="cta-button secondary short text-center"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="cta-button primary short text-center"
            >
              {dependent ? "Update Dependent" : "Add Dependent"}
            </button>
          </div>
        </form>
      </PopupContent>
    </ModalNew>
  );
}

export default DependentModal;
